import { Expense, ExpenseCategory, ExpenseStats, RiskFactor } from '../types/expense';

export function calculateAuditRisk(expenses: Expense[], income: number): ExpenseStats {
  const expensesByCategory: Record<ExpenseCategory, number> = {
    'advertising': 0,
    'vehicle': 0,
    'commissions': 0,
    'contract-labor': 0,
    'depreciation': 0,
    'insurance': 0,
    'interest': 0,
    'legal-professional': 0,
    'office-expense': 0,
    'rent-lease': 0,
    'repairs-maintenance': 0,
    'supplies': 0,
    'taxes-licenses': 0,
    'travel': 0,
    'meals': 0,
    'utilities': 0,
    'home-office': 0,
    'other': 0
  };

  let totalExpenses = 0;

  // Sum up expenses by category
  expenses.forEach(expense => {
    expensesByCategory[expense.category] += expense.amount;
    totalExpenses += expense.amount;
  });

  const riskFactors: RiskFactor[] = [];
  let riskScore = 0;

  // Income validation
  if (income === 0) {
    return {
      totalExpenses,
      expensesByCategory,
      auditRiskScore: 0,
      riskFactors: []
    };
  }

  const expenseRatio = totalExpenses / income;

  // Risk Factor 1: Total expense ratio
  if (expenseRatio > 0.8) {
    riskScore += 30;
    riskFactors.push({
      category: 'Overall',
      issue: `Total expenses are ${(expenseRatio * 100).toFixed(0)}% of income`,
      severity: 'high',
      recommendation: 'Expenses over 80% of income may trigger IRS scrutiny. Ensure all deductions are legitimate and well-documented.'
    });
  } else if (expenseRatio > 0.6) {
    riskScore += 15;
    riskFactors.push({
      category: 'Overall',
      issue: `Total expenses are ${(expenseRatio * 100).toFixed(0)}% of income`,
      severity: 'medium',
      recommendation: 'High expense ratio. Make sure you have receipts and business purpose documentation for all expenses.'
    });
  }

  // Risk Factor 2: Home office deduction
  const homeOfficeRatio = expensesByCategory['home-office'] / income;
  if (expensesByCategory['home-office'] > 0 && homeOfficeRatio > 0.3) {
    riskScore += 25;
    riskFactors.push({
      category: 'Home Office',
      issue: 'Home office deduction is unusually high',
      severity: 'high',
      recommendation: 'Ensure you meet IRS requirements: exclusive and regular use for business. Keep detailed records of square footage and total home expenses.'
    });
  } else if (expensesByCategory['home-office'] > 0 && homeOfficeRatio > 0.15) {
    riskScore += 10;
    riskFactors.push({
      category: 'Home Office',
      issue: 'Home office deduction requires careful documentation',
      severity: 'medium',
      recommendation: 'Document that your home office is used exclusively for business and calculate based on actual square footage percentage.'
    });
  }

  // Risk Factor 3: Vehicle expenses
  const vehicleRatio = expensesByCategory['vehicle'] / income;
  if (expensesByCategory['vehicle'] > 0 && vehicleRatio > 0.25) {
    riskScore += 20;
    riskFactors.push({
      category: 'Vehicle',
      issue: 'Vehicle expenses are high relative to income',
      severity: 'high',
      recommendation: 'Keep detailed mileage logs with dates, destinations, and business purposes. IRS closely audits vehicle deductions.'
    });
  } else if (expensesByCategory['vehicle'] > 15000) {
    riskScore += 10;
    riskFactors.push({
      category: 'Vehicle',
      issue: 'Large vehicle expense amount',
      severity: 'medium',
      recommendation: 'Document business vs personal use percentage. Consider using the standard mileage rate if you don\'t have detailed actual expense records.'
    });
  }

  // Risk Factor 4: Meals & Entertainment
  const mealsRatio = expensesByCategory['meals'] / income;
  if (expensesByCategory['meals'] > 0 && mealsRatio > 0.15) {
    riskScore += 15;
    riskFactors.push({
      category: 'Meals',
      issue: 'Meals & entertainment expenses are high',
      severity: 'medium',
      recommendation: 'For each meal, document: date, location, attendees, business relationship, and topics discussed. Only 50% is deductible.'
    });
  }

  // Risk Factor 5: Travel expenses
  const travelRatio = expensesByCategory['travel'] / income;
  if (expensesByCategory['travel'] > 0 && travelRatio > 0.2) {
    riskScore += 15;
    riskFactors.push({
      category: 'Travel',
      issue: 'Travel expenses are high relative to income',
      severity: 'medium',
      recommendation: 'Keep records showing business purpose, dates, destinations, and that trips were primarily for business (more than 50% business days).'
    });
  }

  // Risk Factor 6: Cash-intensive expenses
  const cashIntensiveTotal = expensesByCategory['meals'] + expensesByCategory['travel'] + expensesByCategory['other'];
  if (cashIntensiveTotal > totalExpenses * 0.4) {
    riskScore += 10;
    riskFactors.push({
      category: 'Documentation',
      issue: 'High percentage of hard-to-verify expense categories',
      severity: 'medium',
      recommendation: 'Use credit cards when possible for better documentation. Keep all receipts and detailed notes for cash expenses.'
    });
  }

  // Risk Factor 7: Round numbers (indicates estimates)
  const roundNumbers = expenses.filter(e => e.amount % 100 === 0 || e.amount % 50 === 0).length;
  if (roundNumbers > expenses.length * 0.3) {
    riskScore += 10;
    riskFactors.push({
      category: 'Documentation',
      issue: 'Many expenses are round numbers',
      severity: 'low',
      recommendation: 'Round numbers suggest estimates. Use actual amounts from receipts to improve audit defense.'
    });
  }

  // Risk Factor 8: Missing business purpose
  const missingPurpose = expenses.filter(e => !e.businessPurpose || e.businessPurpose.trim().length < 10).length;
  if (missingPurpose > expenses.length * 0.3) {
    riskScore += 15;
    riskFactors.push({
      category: 'Documentation',
      issue: `${missingPurpose} expenses lack detailed business purpose`,
      severity: 'high',
      recommendation: 'Add specific business purpose notes to all expenses. IRS wants to see clear connection to income-generating activities.'
    });
  }

  // Cap risk score at 100
  riskScore = Math.min(riskScore, 100);

  // Add positive factors if low risk
  if (riskScore < 30) {
    riskFactors.push({
      category: 'Overall',
      issue: 'Low audit risk profile',
      severity: 'low',
      recommendation: 'Your expense ratios look reasonable. Continue maintaining good documentation and you should be well-prepared if audited.'
    });
  }

  return {
    totalExpenses,
    expensesByCategory,
    auditRiskScore: riskScore,
    riskFactors
  };
}

export function getRiskLevel(score: number): { label: string; color: string; description: string } {
  if (score >= 70) {
    return {
      label: 'High Risk',
      color: 'red',
      description: 'Multiple red flags. Review documentation carefully.'
    };
  } else if (score >= 40) {
    return {
      label: 'Medium Risk',
      color: 'yellow',
      description: 'Some concerns. Improve documentation in flagged areas.'
    };
  } else if (score >= 20) {
    return {
      label: 'Low-Medium Risk',
      color: 'blue',
      description: 'Fairly safe profile with minor areas to improve.'
    };
  } else {
    return {
      label: 'Low Risk',
      color: 'green',
      description: 'Conservative approach. Well-positioned for an audit.'
    };
  }
}
